import { createSlice } from "@reduxjs/toolkit";

export const ieltsAttemptSlice = createSlice({
  name: "ieltsAttempt",
  initialState: {
    // Attempt metadata
    attemptId: null,
    examId: null,
    attemptType: null, // 'full' | 'listening' | 'reading' | 'writing' | 'speaking'
    startTime: null,

    // Current position
    currentSection: null, // 'listening' | 'reading' | 'writing' | 'speaking'
    sectionIndex: 0,
    currentPartIndex: 0,
    currentQuestionIndex: 0,

    // Answers storage
    answers: {
      listening: [],
      reading: [],
      writing: { task1: '', task2: '' },
      speaking: { part1: null, part2: null, part3: null }
    },

    // Question management
    markedQuestions: [],
    attemptedQuestions: [],

    // Timing
    timeRemaining: {
      listening: 40 * 60, // 40 minutes in seconds
      reading: 60 * 60,   // 60 minutes
      writing: 60 * 60,   // 60 minutes
      speaking: 15 * 60,  // 15 minutes
      overall: 0
    },

    // Section completion status
    sectionCompletionStatus: {
      listening: false,
      reading: false,
      writing: false,
      speaking: false
    },

    // Full exam specific
    isOnBreak: false,
    breakTimeRemaining: 0,

    // Pause/Resume
    isPaused: false,
    pauseReason: null,
    pauseStartTime: null,

    // Audio states (for listening)
    audioPlaybackState: {
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      currentPartAudio: null
    },

    // Audio playback tracking (IELTS rules enforcement)
    audioPlaybackTracking: {
      playCount: {
        part1: 0,
        part2: 0,
        part3: 0,
        part4: 0
      },
      completed: {
        part1: false,
        part2: false,
        part3: false,
        part4: false
      },
      canReplay: false // IELTS rule: audio plays once only
    },

    // Initialization state (pre-test setup)
    initialization: {
      audioCheckCompleted: false,
      instructionsRead: false,
      browserCompatible: null,
      testStarted: false,
      equipmentConfirmed: false,
      initializationTimestamp: null
    },

    // Review period state (after audio completion)
    reviewPeriod: {
      isActive: false,
      duration: 30, // seconds
      timeRemaining: 30,
      currentPart: null,
      unansweredCount: 0
    },

    // Part locking (prevent backward navigation)
    partLockStatus: {
      part1: false,
      part2: false,
      part3: false,
      part4: false
    },

    // Recording states (for speaking)
    recordingState: {
      isRecording: false,
      isPaused: false,
      duration: 0,
      currentPartRecording: null,
      recordings: {}
    },

    // Writing specific
    writingContent: {
      task1: '',
      task2: ''
    },
    wordCounts: {
      task1: 0,
      task2: 0
    },

    // Auto-save
    autoSaveStatus: 'saved', // 'saved' | 'saving' | 'error'
    lastSavedAt: null,

    // Session logs
    sessionLogs: [],

    // Exam data
    examData: null
  },

  reducers: {
    // Initialize attempt
    initializeAttempt: (state, action) => {
      const { attemptId, examId, attemptType, examData } = action.payload;
      state.attemptId = attemptId;
      state.examId = examId;
      state.attemptType = attemptType;
      state.examData = examData;
      state.startTime = Date.now();

      // Set current section based on attempt type
      if (attemptType === 'full') {
        state.currentSection = 'listening';
      } else {
        state.currentSection = attemptType;
      }

      // Reset audio playback tracking for fresh attempt
      state.audioPlaybackTracking = {
        playCount: { part1: 0, part2: 0, part3: 0, part4: 0 },
        completed: { part1: false, part2: false, part3: false, part4: false },
        canReplay: false
      };

      // Reset part lock status
      state.partLockStatus = { part1: false, part2: false, part3: false, part4: false };

      // Reset review period
      state.reviewPeriod = { isActive: false, duration: 30, timeRemaining: 30, currentPart: null, unansweredCount: 0 };

      // Reset navigation
      state.currentPartIndex = 0;
      state.currentQuestionIndex = 0;

      // Reset marked questions
      state.markedQuestions = [];

      // Initialize answers arrays based on exam data
      if (examData?.sections) {
        examData.sections.forEach(section => {
          const sectionType = section.type.toLowerCase();
          if (sectionType === 'listening' || sectionType === 'reading') {
            state.answers[sectionType] = new Array(section.questionCount).fill(null);
          } else if (sectionType === 'writing') {
            state.answers.writing = { task1: '', task2: '' };
          } else if (sectionType === 'speaking') {
            state.answers.speaking = { part1: null, part2: null, part3: null };
          }
        });
      }
    },

    // Navigation
    setCurrentSection: (state, action) => {
      state.currentSection = action.payload;
      state.currentPartIndex = 0;
      state.currentQuestionIndex = 0;
    },

    setCurrentPartIndex: (state, action) => {
      state.currentPartIndex = action.payload;
    },

    setCurrentQuestionIndex: (state, action) => {
      state.currentQuestionIndex = action.payload;
    },

    // Answer management
    setAnswer: (state, action) => {
      const { section, questionIndex, answer } = action.payload;
      if (section === 'listening' || section === 'reading') {
        state.answers[section][questionIndex] = answer;
      } else if (section === 'writing') {
        const { task, content } = action.payload;
        state.answers.writing[task] = content;
      } else if (section === 'speaking') {
        const { part, recording } = action.payload;
        state.answers.speaking[part] = recording;
      }
      state.autoSaveStatus = 'saving';
    },

    setAllAnswers: (state, action) => {
      state.answers = { ...state.answers, ...action.payload };
    },

    // Question marking
    toggleMarkQuestion: (state, action) => {
      const questionId = action.payload;
      const index = state.markedQuestions.indexOf(questionId);
      if (index > -1) {
        state.markedQuestions.splice(index, 1);
      } else {
        state.markedQuestions.push(questionId);
      }
    },

    addAttemptedQuestion: (state, action) => {
      const questionId = action.payload;
      if (!state.attemptedQuestions.includes(questionId)) {
        state.attemptedQuestions.push(questionId);
      }
    },

    // Timing
    setTimeRemaining: (state, action) => {
      const { section, time } = action.payload;
      if (section) {
        state.timeRemaining[section] = time;
      } else {
        state.timeRemaining.overall = time;
      }
    },

    decrementTime: (state, action) => {
      const section = action.payload || state.currentSection;
      if (section && state.timeRemaining[section] > 0) {
        state.timeRemaining[section] -= 1;
      }
      if (state.timeRemaining.overall > 0) {
        state.timeRemaining.overall -= 1;
      }
    },

    // Section completion
    markSectionComplete: (state, action) => {
      const section = action.payload;
      state.sectionCompletionStatus[section] = true;
    },

    // Break management
    startBreak: (state, action) => {
      state.isOnBreak = true;
      state.breakTimeRemaining = action.payload || 600; // Default 10 minutes
    },

    endBreak: (state) => {
      state.isOnBreak = false;
      state.breakTimeRemaining = 0;
    },

    decrementBreakTime: (state) => {
      if (state.breakTimeRemaining > 0) {
        state.breakTimeRemaining -= 1;
      }
    },

    // Pause/Resume
    pauseAttempt: (state, action) => {
      state.isPaused = true;
      state.pauseReason = action.payload?.reason || null;
      state.pauseStartTime = Date.now();
    },

    resumeAttempt: (state) => {
      state.isPaused = false;
      state.pauseReason = null;
      state.pauseStartTime = null;
    },

    // Audio playback (Listening)
    setAudioPlaybackState: (state, action) => {
      state.audioPlaybackState = { ...state.audioPlaybackState, ...action.payload };
    },

    // Audio playback tracking (IELTS enforcement)
    incrementAudioPlayCount: (state, action) => {
      const partKey = action.payload; // e.g., 'part1', 'part2'
      if (state.audioPlaybackTracking.playCount[partKey] !== undefined) {
        state.audioPlaybackTracking.playCount[partKey] += 1;
      }
    },

    markAudioComplete: (state, action) => {
      const partKey = action.payload; // e.g., 'part1', 'part2'
      if (state.audioPlaybackTracking.completed[partKey] !== undefined) {
        state.audioPlaybackTracking.completed[partKey] = true;
      }
    },

    setCanReplayAudio: (state, action) => {
      state.audioPlaybackTracking.canReplay = action.payload;
    },

    // Initialization
    setAudioCheckCompleted: (state, action) => {
      state.initialization.audioCheckCompleted = action.payload;
      if (action.payload) {
        state.initialization.equipmentConfirmed = true;
      }
    },

    setInstructionsRead: (state, action) => {
      state.initialization.instructionsRead = action.payload;
    },

    setBrowserCompatible: (state, action) => {
      state.initialization.browserCompatible = action.payload;
    },

    setTestStarted: (state, action) => {
      state.initialization.testStarted = action.payload;
      if (action.payload) {
        state.initialization.initializationTimestamp = Date.now();
      }
    },

    setEquipmentConfirmed: (state, action) => {
      state.initialization.equipmentConfirmed = action.payload;
    },

    // Review Period
    startReviewPeriod: (state, action) => {
      const { partNumber, unansweredCount, duration = 30 } = action.payload;
      state.reviewPeriod.isActive = true;
      state.reviewPeriod.duration = duration;
      state.reviewPeriod.timeRemaining = duration;
      state.reviewPeriod.currentPart = partNumber;
      state.reviewPeriod.unansweredCount = unansweredCount;
    },

    endReviewPeriod: (state) => {
      state.reviewPeriod.isActive = false;
      state.reviewPeriod.timeRemaining = 0;
      state.reviewPeriod.currentPart = null;
    },

    decrementReviewTime: (state) => {
      if (state.reviewPeriod.timeRemaining > 0) {
        state.reviewPeriod.timeRemaining -= 1;
      }
      if (state.reviewPeriod.timeRemaining === 0) {
        state.reviewPeriod.isActive = false;
      }
    },

    // Part Locking
    lockPart: (state, action) => {
      const partKey = action.payload; // e.g., 'part1', 'part2'
      if (state.partLockStatus[partKey] !== undefined) {
        state.partLockStatus[partKey] = true;
      }
    },

    unlockAllParts: (state) => {
      state.partLockStatus = {
        part1: false,
        part2: false,
        part3: false,
        part4: false
      };
    },

    // Recording (Speaking)
    setRecordingState: (state, action) => {
      state.recordingState = { ...state.recordingState, ...action.payload };
    },

    saveRecording: (state, action) => {
      const { part, recording } = action.payload;
      state.recordingState.recordings[part] = recording;
    },

    // Writing content
    setWritingContent: (state, action) => {
      const { task, content } = action.payload;
      state.writingContent[task] = content;
      state.autoSaveStatus = 'saving';
    },

    setWordCount: (state, action) => {
      const { task, count } = action.payload;
      state.wordCounts[task] = count;
    },

    // Auto-save
    setAutoSaveStatus: (state, action) => {
      state.autoSaveStatus = action.payload;
      if (action.payload === 'saved') {
        state.lastSavedAt = Date.now();
      }
    },

    // Session logs
    addSessionLog: (state, action) => {
      state.sessionLogs.push({
        timestamp: Date.now(),
        ...action.payload
      });
    },

    // Exam data
    setExamData: (state, action) => {
      state.examData = action.payload;
    },

    // Reset
    resetAttempt: (state) => {
      return {
        ...ieltsAttemptSlice.getInitialState()
      };
    }
  }
});

export const {
  initializeAttempt,
  setCurrentSection,
  setCurrentPartIndex,
  setCurrentQuestionIndex,
  setAnswer,
  setAllAnswers,
  toggleMarkQuestion,
  addAttemptedQuestion,
  setTimeRemaining,
  decrementTime,
  markSectionComplete,
  startBreak,
  endBreak,
  decrementBreakTime,
  pauseAttempt,
  resumeAttempt,
  setAudioPlaybackState,
  incrementAudioPlayCount,
  markAudioComplete,
  setCanReplayAudio,
  setAudioCheckCompleted,
  setInstructionsRead,
  setBrowserCompatible,
  setTestStarted,
  setEquipmentConfirmed,
  startReviewPeriod,
  endReviewPeriod,
  decrementReviewTime,
  lockPart,
  unlockAllParts,
  setRecordingState,
  saveRecording,
  setWritingContent,
  setWordCount,
  setAutoSaveStatus,
  addSessionLog,
  setExamData,
  resetAttempt
} = ieltsAttemptSlice.actions;

// Selectors
export const selectAttemptId = (state) => state.ieltsAttempt.attemptId;
export const selectCurrentSection = (state) => state.ieltsAttempt.currentSection;
export const selectCurrentPartIndex = (state) => state.ieltsAttempt.currentPartIndex;
export const selectCurrentQuestionIndex = (state) => state.ieltsAttempt.currentQuestionIndex;
export const selectAnswers = (state) => state.ieltsAttempt.answers;
export const selectMarkedQuestions = (state) => state.ieltsAttempt.markedQuestions;
export const selectTimeRemaining = (state) => state.ieltsAttempt.timeRemaining;
export const selectSectionCompletionStatus = (state) => state.ieltsAttempt.sectionCompletionStatus;
export const selectIsOnBreak = (state) => state.ieltsAttempt.isOnBreak;
export const selectIsPaused = (state) => state.ieltsAttempt.isPaused;
export const selectAudioPlaybackState = (state) => state.ieltsAttempt.audioPlaybackState;
export const selectRecordingState = (state) => state.ieltsAttempt.recordingState;
export const selectWritingContent = (state) => state.ieltsAttempt.writingContent;
export const selectWordCounts = (state) => state.ieltsAttempt.wordCounts;
export const selectAutoSaveStatus = (state) => state.ieltsAttempt.autoSaveStatus;
export const selectExamData = (state) => state.ieltsAttempt.examData;
export const selectAudioPlaybackTracking = (state) => state.ieltsAttempt.audioPlaybackTracking;
export const selectInitialization = (state) => state.ieltsAttempt.initialization;
export const selectReviewPeriod = (state) => state.ieltsAttempt.reviewPeriod || { isActive: false, duration: 30, timeRemaining: 30, currentPart: null, unansweredCount: 0 };
export const selectPartLockStatus = (state) => state.ieltsAttempt.partLockStatus || { part1: false, part2: false, part3: false, part4: false };

export default ieltsAttemptSlice.reducer;
